<template>
  <div class="mp" ref="root">
    <button type="button" class="mp-trigger" :class="{ open: open }" @click="toggle">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <rect x="2.5" y="3.5" width="15" height="14" rx="2" />
        <path d="M2.5 8h15M6.5 2v3M13.5 2v3" />
        <path d="M6 11h1.5M9.25 11h1.5M12.5 11H14M6 14h1.5M9.25 14h1.5M12.5 14H14" />
      </svg>
      <b>{{ label }}</b>
      <span class="mp-caret">{{ open ? "▲" : "▼" }}</span>
    </button>

    <div v-if="open" class="mp-pop">
      <div class="mp-year">
        <button type="button" class="mp-nav" :disabled="!canPrev" @click="year--">‹</button>
        <b>{{ year }}</b>
        <button type="button" class="mp-nav" :disabled="!canNext" @click="year++">›</button>
      </div>

      <div class="mp-grid">
        <button
          v-for="(m, i) in months"
          :key="m"
          type="button"
          class="mp-m"
          :class="{ sel: isSelected(i), cur: isCurrent(i) }"
          :disabled="isDisabled(i)"
          @click="pick(i)"
        >
          {{ m }}
        </button>
      </div>

      <div class="mp-legend">
        <span><i class="lg lg-sel"></i>Selected</span>
        <span><i class="lg lg-cur"></i>Current month</span>
        <span><i class="lg lg-na"></i>Not available</span>
      </div>

      <div class="mp-note">
        <span class="mp-info"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.04729 4.10352C6.04729 3.74716 6.33885 3.45559 6.69521 3.45559H7.12716C7.48352 3.45559 7.77508 3.74716 7.77508 4.10352V4.53546C7.77508 4.89182 7.48352 5.18339 7.12716 5.18339H6.69521C6.33885 5.18339 6.04729 4.89182 6.04729 4.53546V4.10352ZM8.63898 10.3668H5.18339V9.50288H6.04729V6.91118H5.18339V6.04729H7.77508V9.50288H8.63898V10.3668ZM6.91118 0C3.09424 0 0 3.09424 0 6.91118C0 10.7281 3.09424 13.8224 6.91118 13.8224C10.7281 13.8224 13.8224 10.7281 13.8224 6.91118C13.8224 3.09424 10.7281 0 6.91118 0ZM6.91118 12.5265C3.80992 12.5265 1.29585 10.0124 1.29585 6.91118C1.29585 3.80992 3.80992 1.29585 6.91118 1.29585C10.0124 1.29585 12.5265 3.80992 12.5265 6.91118C12.5265 10.0124 10.0124 12.5265 6.91118 12.5265Z" fill="#D1D1D1"/>
          </svg>
        </span>
        <span>
          Only 1 month can be selected. 
          <!-- Showing data for <b>1–{{ lastDay }} {{ fullMonth }} {{ selYear }}</b>. -->
        </span>
      </div>
    </div>
  </div>
</template>

<script>
const FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// "2026-09" -> { y: 2026, m: 8 }
const parse = (s) => {
  const [y, m] = s.split("-").map(Number);
  return { y, m: m - 1 };
};
const idx = (y, m) => y * 12 + m;

export default {
  name: "MonthPicker",
  props: {
    value: { type: String, required: true },
    min: { type: String, default: "" },
    max: { type: String, default: "" }, 
  },
  data() {
    return { open: false, year: parse(this.value).y, months: FULL.map((m) => m.slice(0, 3)) };
  },
  computed: {
    sel() { return parse(this.value); },
    selYear() { return this.sel.y; },
    fullMonth() { return FULL[this.sel.m]; },
    lastDay() { return new Date(this.sel.y, this.sel.m + 1, 0).getDate(); },
    label() { return `${this.fullMonth} ${this.sel.y}`; },
    now() { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; },
    maxYM() { return this.max ? parse(this.max) : this.now; },
    minYM() { return this.min ? parse(this.min) : null; },
    canNext() { return this.year < this.maxYM.y; },
    canPrev() { return !this.minYM || this.year > this.minYM.y; },
  },
  watch: {
    value() { this.year = this.sel.y; },
  },
  mounted() { document.addEventListener("mousedown", this.onOutside); },
  beforeDestroy() { document.removeEventListener("mousedown", this.onOutside); },
  methods: {
    toggle() { this.open = !this.open; if (this.open) this.year = this.sel.y; },
    onOutside(e) { if (this.open && !this.$refs.root.contains(e.target)) this.open = false; },
    isSelected(i) { return this.year === this.sel.y && i === this.sel.m; },
    isCurrent(i) { return this.year === this.now.y && i === this.now.m; },
    isDisabled(i) {
      const v = idx(this.year, i);
      if (v > idx(this.maxYM.y, this.maxYM.m)) return true;
      return !!this.minYM && v < idx(this.minYM.y, this.minYM.m);
    },
    pick(i) {
      const v = `${this.year}-${String(i + 1).padStart(2, "0")}`;
      this.$emit("input", v);
      this.$emit("change", v);
      this.open = false;
    },
  },
};
</script>

<style scoped>
.mp { position: relative; display: inline-block; }
.mp-trigger { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #dde2e7; border-radius: 6px;
  padding: 8px 14px; font-size: 13px; cursor: pointer; color: #1f2933; }
.mp-trigger.open { border-color: #3b7ddd; }
.mp-caret { font-size: 9px; margin-left: 6px; }
.mp-pop { position: absolute; right: 0; top: calc(100% + 8px); width: 400px; background: #fff; border-radius: 10px; z-index: 50;
  box-shadow: 0 6px 24px rgba(0,0,0,.15); padding: 18px 22px 20px; }
.mp-year { display: flex; justify-content: space-between; align-items: center; font-size: 16px; margin-bottom: 14px; }
.mp-nav { border: 0; background: none; font-size: 20px; color: #6b7680; cursor: pointer; padding: 0 8px; }
.mp-nav:disabled { color: #d3d8dd; cursor: default; }
.mp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px 8px; margin-bottom: 14px; }
.mp-m { height: 40px; border: 1px solid transparent; border-radius: 5px; background: none; font-size: 13px; color: #3c4650; cursor: pointer; }
.mp-m:hover:not(:disabled):not(.sel) { background: #f1f5fb; }
.mp-m.cur { border-color: #3b7ddd; color: #3b7ddd; }
.mp-m.sel { background: #3b7ddd; border-color: #3b7ddd; color: #fff; font-weight: 600; }
.mp-m:disabled { color: #c9cfd5; cursor: default; }
.mp-legend { display: flex; justify-content: space-between; font-size: 11px; color: #6b7680; margin-bottom: 14px; }
.lg { display: inline-block; width: 11px; height: 11px; border-radius: 2px; margin-right: 6px; vertical-align: -1px; }
.lg-sel { background: #3b7ddd; }
.lg-cur { border: 1.5px solid #3b7ddd; }
.lg-na { border: 1.5px solid #cfd5da; }
.mp-note { display: flex; gap: 8px; background: #f2f4f5; border-radius: 6px; padding: 10px 12px; font-size: 12px; color: #4b5560; line-height: 1.5; }
.mp-info { color: #a9b2ba; }
</style>